import { useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { Folder } from '@volt/shared';
import { useSession } from '../state/session.js';

/**
 * Module-level cache so folders are available immediately on first render
 * and don't flash empty while the request is in flight.
 */
let cachedFolders: Folder[] = [];

export interface FoldersState {
  folders: Folder[];
  loading: boolean;
  setFolders: Dispatch<SetStateAction<Folder[]>>;
}

/**
 * Manages the server-backed folder list for the current user.
 *
 * - Initialises from the module-level cache so the view is never empty on
 *   first render.
 * - Refetches from the API whenever `version` changes (driven by UIContext's
 *   `foldersVersion` counter, which is incremented after every mutation and
 *   whenever the drawer opens).
 */
export function useFolders(version: number): FoldersState {
  const { api } = useSession();
  const [folders, setFolders] = useState<Folder[]>(cachedFolders);
  const [loading, setLoading] = useState(cachedFolders.length === 0);
  const versionRef = useRef<number>(-1);

  useEffect(() => {
    if (versionRef.current === version) return;
    versionRef.current = version;

    let cancelled = false;
    setLoading(true);

    api
      .listFolders()
      .then(({ folders: data }) => {
        if (cancelled) return;
        cachedFolders = data;
        setFolders(data);
      })
      .catch(() => {
        // keep existing state on error
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, version]);

  // Keep module cache in sync with local mutations (create/rename/delete/reorder)
  useEffect(() => {
    cachedFolders = folders;
  }, [folders]);

  return { folders, loading, setFolders };
}
